import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowsClockwise, FileText, Package, Prohibit, Truck,
  UploadSimple, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  bookShipments,
  cancelShipment,
  createCustomsCase,
  fetchAccessMe,
  fetchQuoteShipping,
  financeSyncShipment,
  reconcileShipmentBooking,
  syncShipmentDocuments,
  trackShipment,
  updateCustomsStatus,
  uploadCustomsDocument,
} from "@/lib/api";


const EXCEPTION_STATUSES = new Set([
  "AddressFailed", "NoRate", "BookingFailed", "CustomsFailed",
  "TrackingException", "PodMissing", "FinanceFailed",
]);

const CANCELLABLE_STATUSES = new Set([
  "Draft", "OriginSelected", "AddressValidated", "Rated",
  "RateSelectionRequired", "RateSelected", "BookingFailed",
  "Booked", "CustomsPending", "CustomsSubmitted", "CustomsManual",
  "CustomsFailed", "ReadyToShip",
]);

const formatError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || error?.message || fallback;
};

const documentUrl = (document) => document?.url || null;

const directOverride = () => {
  if (typeof window === "undefined") return null;
  const value = (new URLSearchParams(window.location.search).get("direct") || "")
    .trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(value)) return true;
  if (["0", "false", "no", "off"].includes(value)) return false;
  return null;
};


export default function ShipmentOperationsPanel({
  quoteId, isSuperAdmin = false, refreshToken = 0,
}) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState("");
  const [cancelTarget, setCancelTarget] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelConfirmation, setCancelConfirmation] = useState("");
  const [reconcileTarget, setReconcileTarget] = useState("");
  const [providerReference, setProviderReference] = useState("");
  const [customsTarget, setCustomsTarget] = useState("");
  const [customsOutcome, setCustomsOutcome] = useState("cleared");
  const [customsReference, setCustomsReference] = useState("");
  const [customsNote, setCustomsNote] = useState("");
  const [bookingEligibility, setBookingEligibility] = useState(null);
  const [financeSyncEnabled, setFinanceSyncEnabled] = useState(false);
  const [access, setAccess] = useState(null);
  const [chargesAcknowledged, setChargesAcknowledged] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quoteId) return;
    if (!quiet) setLoading(true);
    try {
      const [shippingResult, accessResult] = await Promise.allSettled([
        fetchQuoteShipping(quoteId, { direct: directOverride() }),
        fetchAccessMe(),
      ]);
      if (shippingResult.status === "rejected") throw shippingResult.reason;
      const result = shippingResult.value;
      setShipments(result?.shipments || []);
      setBookingEligibility(result?.booking_eligibility || null);
      setFinanceSyncEnabled(!!result?.finance_sync_enabled);
      setAccess(accessResult.status === "fulfilled"
        ? (accessResult.value || null)
        : null);
    } catch (error) {
      if (![404, 503].includes(error?.response?.status)) {
        toast.error(formatError(error, "Could not load shipment operations"));
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const runAction = useCallback(async (shipmentId, label, action) => {
    setActionId(`${shipmentId}:${label}`);
    try {
      const result = await action();
      await load({ quiet: true });
      if (result?.warning) {
        toast.warning(result.warning);
      } else {
        toast.success(result?.pending
          ? "Cancellation queued until the carrier load is ready"
          : label);
      }
      return result;
    } catch (error) {
      toast.error(formatError(error, `${label} failed`));
    } finally {
      setActionId("");
    }
  }, [load]);

  const hasShipments = shipments.length > 0;
  const canManageShipping = !!access?.is_super_admin
    || (access?.permissions || []).includes("shipping.manage");
  const unbookedShipments = shipments.filter((shipment) =>
    shipment.status !== "Cancelled"
    && shipment.booking?.status !== "confirmed"
    && shipment.booking?.status !== "cancelled");
  const allUnbookedRatesSelected = unbookedShipments.length > 0
    && unbookedShipments.every((shipment) => shipment.selected_rate_id);
  const exceptionCount = useMemo(
    () => shipments.filter((shipment) =>
      EXCEPTION_STATUSES.has(shipment.status)
      || shipment.booking?.status === "reconciliation_required").length,
    [shipments],
  );

  if (!hasShipments && !loading) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-sm"
             data-testid="shipment-operations-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <Truck size={15} weight="duotone" className="text-slate-600" />
          <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">
            Shipments
          </h2>
          {exceptionCount > 0 && (
            <span className="text-[10px] font-bold text-rose-700">
              {exceptionCount} exception{exceptionCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <button type="button" onClick={() => load()}
                disabled={loading}
                title="Refresh shipments"
                className="p-1 text-slate-500 hover:text-blue-700 disabled:opacity-50">
          <ArrowsClockwise size={14} weight="bold"
                           className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {unbookedShipments.length > 0 && (
        <div className="px-3 py-3 border-b border-slate-200 bg-slate-50"
             data-testid="shipment-booking-controls">
          <div className="text-xs font-bold text-slate-800">
            Purchase labels / create loads
          </div>
          <div className="mt-1 text-[11px] text-slate-600">
            {unbookedShipments.length} unbooked shipment group{unbookedShipments.length === 1 ? "" : "s"}.
          </div>
          {!allUnbookedRatesSelected && (
            <div className="mt-2 text-[11px] text-amber-800">
              Select a current rate for every unbooked group before creating carrier charges.
            </div>
          )}
          {bookingEligibility && !bookingEligibility.eligible && (
            <div className="mt-2 border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
              {(bookingEligibility.reasons || []).map((reason) => (
                <div key={reason.code} className="flex items-start gap-1">
                  <WarningCircle size={12} className="mt-0.5 shrink-0" />
                  <span>{reason.message}</span>
                </div>
              ))}
            </div>
          )}
          {!canManageShipping && (
            <div className="mt-2 text-[11px] text-slate-500">
              The <span className="font-mono">shipping.manage</span> permission is required to create carrier charges.
            </div>
          )}
          {canManageShipping && bookingEligibility?.eligible
            && allUnbookedRatesSelected && (
            <>
              <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-700">
                <input
                  type="checkbox"
                  checked={chargesAcknowledged}
                  onChange={(event) =>
                    setChargesAcknowledged(event.target.checked)}
                  data-testid="shipment-book-charge-ack"
                  className="mt-0.5"
                />
                I authorize the selected carrier charges and creation of the labels or loads.
              </label>
              <button
                type="button"
                onClick={() => runAction(
                  quoteId, "Carrier booking completed", async () => {
                    let result;
                    try {
                      result = await bookShipments(
                        quoteId, { direct: directOverride() });
                    } finally {
                      setChargesAcknowledged(false);
                    }
                    if (!result?.ok) {
                      const reasons = (result?.shipments || [])
                        .filter((shipment) => !shipment.ok)
                        .map((shipment) => shipment.reason
                          || shipment.error?.message)
                        .filter(Boolean);
                      return {
                        ...result,
                        warning: reasons.join("; ")
                          || (
                            "Some shipment groups were not booked. "
                            + "Completed groups are shown below; resolve the "
                            + "remaining group before retrying."
                          ),
                      };
                    }
                    return result?.closed_loop_ok === false
                      ? {
                        ...result,
                        warning: (
                          "Carrier booking succeeded, but warehouse records "
                          + "are still syncing and will retry automatically."
                        ),
                      }
                      : result;
                  })}
                disabled={!chargesAcknowledged || !!actionId}
                data-testid="shipment-book-carriers"
                className="mt-2 text-[10px] font-semibold px-3 py-1.5 bg-rose-700 text-white rounded-sm disabled:opacity-50"
              >
                Purchase / Create load
              </button>
            </>
          )}
        </div>
      )}

      <div className="divide-y divide-slate-200">
        {shipments.map((shipment) => {
          const booking = shipment.booking || {};
          const documents = shipment.documents || [];
          const labels = [
            ...(booking.package_results || []).map((item) => ({
              type: "label", url: item.label_url,
            })),
            { type: "label", url: booking.label_url },
          ].filter((item) => item.url);
          const availableDocuments = [
            ...documents.filter((document) => documentUrl(document)),
            ...labels,
          ];
          const exception = EXCEPTION_STATUSES.has(shipment.status)
            || booking.status === "reconciliation_required"
            || shipment.business_projection_status === "failed";
          const sid = shipment.shipment_id;
          const submittedCustomsDocuments = new Set(
            shipment.customs?.submitted_documents || [],
          );
          const pendingCustomsDocuments = (
            shipment.customs?.required_documents || []
          ).filter((type) => !submittedCustomsDocuments.has(type));
          return (
            <div key={sid} className="px-3 py-3"
                 data-testid={`shipment-operation-${sid}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Package size={13} weight="bold"
                             className="text-slate-500" />
                    <span className="font-mono text-xs text-slate-800">
                      {sid?.slice(-8)}
                    </span>
                    <span className="text-[10px] font-bold uppercase text-slate-500">
                      {shipment.mode}
                    </span>
                    <span className={`text-[10px] font-bold ${
                      exception ? "text-rose-700" : "text-emerald-700"
                    }`}>
                      {booking.status === "reconciliation_required"
                        ? "Carrier review required"
                        : shipment.status}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600">
                    {shipment.selected_rate?.carrier_name
                      || booking.provider || "Carrier pending"}
                    {shipment.selected_rate?.service_level
                      ? ` · ${shipment.selected_rate.service_level}` : ""}
                  </div>
                  <div className="mt-0.5 text-[11px] font-mono text-slate-600">
                    {booking.tracking_code
                      || booking.pro_number
                      || booking.provider_load_id
                      || "Tracking pending"}
                  </div>
                  {shipment.customs && (
                    <div className="mt-1 text-[11px] text-slate-600">
                      Customs: {shipment.customs.status}
                    </div>
                  )}
                  {(booking.last_error
                    || shipment.business_projection_error || exception) && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] text-rose-700">
                      <WarningCircle size={12} className="mt-0.5 shrink-0" />
                      <span>{booking.last_error
                        || shipment.business_projection_error
                        || shipment.status}</span>
                    </div>
                  )}
                </div>
              </div>

              {availableDocuments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {availableDocuments.map((document, index) => (
                    <a key={`${document.type}-${index}`}
                       href={documentUrl(document) || document.url}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline">
                      <FileText size={11} />
                      {(document.type || "document").replaceAll("_", " ")}
                    </a>
                  ))}
                </div>
              )}

              {canManageShipping && pendingCustomsDocuments.length > 0 && (
                <div className="mt-2 border border-amber-200 bg-amber-50 px-2 py-1.5">
                  <div className="text-[10px] font-bold uppercase text-amber-900">
                    Customs documents pending
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {pendingCustomsDocuments.map((documentType) => (
                      <label
                        key={documentType}
                        title={`Upload ${documentType.replaceAll("_", " ")}`}
                        className={`inline-flex items-center gap-1 border border-amber-300 bg-white px-2 py-1 text-[10px] font-semibold text-amber-900 rounded-sm ${
                          actionId ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-amber-100"
                        }`}
                      >
                        <UploadSimple size={11} weight="bold" />
                        {documentType.replaceAll("_", " ")}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          disabled={!!actionId}
                          className="sr-only"
                          data-testid={`customs-upload-${sid}-${documentType}`}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (!file) return;
                            runAction(
                              sid,
                              `${documentType.replaceAll("_", " ")} uploaded`,
                              () => uploadCustomsDocument(
                                sid, documentType, file),
                            );
                          }}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                {booking.status === "confirmed" && (
                  <button type="button"
                          onClick={() => runAction(
                            sid, "Tracking refreshed",
                            () => trackShipment(sid))}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                    Track
                  </button>
                )}
                {booking.status === "confirmed" && (
                  <button type="button"
                          onClick={() => runAction(
                            sid, "Documents synced",
                            () => syncShipmentDocuments(sid))}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                    Sync documents
                  </button>
                )}
                {canManageShipping
                  && shipment.status === "CustomsPending" && (
                  <button type="button"
                          onClick={() => runAction(
                            sid, "Customs case submitted",
                            () => createCustomsCase(sid))}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-50 disabled:opacity-50">
                    Submit customs
                  </button>
                )}
                {canManageShipping && shipment.customs
                  && ["CustomsPending", "CustomsManual", "CustomsSubmitted", "CustomsFailed"].includes(
                    shipment.status) && (
                  <button type="button"
                          onClick={() => {
                            setCustomsTarget(sid);
                            setCustomsOutcome(
                              ["draft", "creating", "reconciliation_required", "rejected"]
                                .includes(shipment.customs.status)
                                ? "submitted" : "cleared");
                            setCustomsReference("");
                            setCustomsNote("");
                          }}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-blue-300 text-blue-800 rounded-sm hover:bg-blue-50 disabled:opacity-50">
                    Update customs
                  </button>
                )}
                {financeSyncEnabled && isSuperAdmin
                  && ["PodReceived", "FinanceFailed"].includes(
                  shipment.status) && (
                  <button type="button"
                          onClick={() => runAction(
                            sid, "Finance sync completed",
                            () => financeSyncShipment(sid))}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                    Finance sync
                  </button>
                )}
                {isSuperAdmin && CANCELLABLE_STATUSES.has(shipment.status) && (
                  <button type="button"
                          onClick={() => {
                            setCancelTarget(sid);
                            setCancelReason("");
                            setCancelConfirmation("");
                          }}
                          disabled={!!actionId}
                          title="Cancel shipment"
                          className="p-1 border border-rose-200 text-rose-700 rounded-sm hover:bg-rose-50 disabled:opacity-50">
                    <Prohibit size={12} weight="bold" />
                  </button>
                )}
                {isSuperAdmin
                  && booking.status === "reconciliation_required" && (
                  <button type="button"
                          onClick={() => {
                            setReconcileTarget(sid);
                            setProviderReference("");
                          }}
                          disabled={!!actionId}
                          className="text-[10px] font-semibold px-2 py-1 border border-rose-300 text-rose-800 rounded-sm hover:bg-rose-50 disabled:opacity-50">
                    Resolve carrier check
                  </button>
                )}
              </div>

              {customsTarget === sid && (
                <div className="mt-2 border-t border-slate-200 pt-2 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select value={customsOutcome}
                            onChange={(event) => setCustomsOutcome(event.target.value)}
                            className="h-7 text-[11px] border border-slate-300 rounded-sm px-2 bg-white">
                      <option value="submitted">Ticket verified</option>
                      <option value="cleared">Cleared</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    {customsOutcome !== "rejected" && (
                      <input value={customsReference}
                             onChange={(event) => setCustomsReference(event.target.value)}
                             placeholder={customsOutcome === "submitted"
                               ? "Clearit ticket ID" : "Clearance reference"}
                             className="min-w-44 flex-1 h-7 text-[11px] border border-slate-300 rounded-sm px-2" />
                    )}
                  </div>
                  <input value={customsNote}
                         onChange={(event) => setCustomsNote(event.target.value)}
                         placeholder={customsOutcome === "rejected"
                           ? "Rejection reason" : "Status note (optional)"}
                         className="w-full h-7 text-[11px] border border-slate-300 rounded-sm px-2" />
                  <div className="flex items-center gap-1.5">
                    <button type="button"
                            onClick={() => runAction(
                              sid, "Customs status updated",
                              async () => {
                                const result = await updateCustomsStatus(sid, {
                                  status: customsOutcome,
                                  provider_reference: customsReference.trim(),
                                  note: customsNote.trim(),
                                });
                                setCustomsTarget("");
                                return result;
                              })}
                            disabled={(customsOutcome !== "rejected"
                              && !customsReference.trim())
                              || (customsOutcome === "rejected"
                                && !customsNote.trim())
                              || !!actionId}
                            className="text-[10px] font-semibold px-2 py-1 bg-blue-700 text-white rounded-sm disabled:opacity-50">
                      Save status
                    </button>
                    <button type="button"
                            onClick={() => setCustomsTarget("")}
                            className="text-[10px] px-2 py-1 border border-slate-300 rounded-sm">
                      Close
                    </button>
                  </div>
                </div>
              )}

              {reconcileTarget === sid && (
                <div className="mt-2 border-t border-slate-200 pt-2">
                  <div className="text-[11px] font-semibold text-slate-700">
                    Result from carrier portal
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <input value={providerReference}
                           onChange={(event) =>
                             setProviderReference(event.target.value)}
                           placeholder="Existing shipment / order / load ID"
                           className="min-w-48 flex-1 text-[11px] border border-slate-300 rounded-sm px-2 py-1" />
                    <button type="button"
                            onClick={() => runAction(
                              sid, "Existing carrier booking recorded",
                              async () => {
                                const isEasyPostOrder =
                                  booking.provider === "easypost"
                                  && Boolean(booking.selected_rate_snapshot?.provider_order_id);
                                const key = isEasyPostOrder
                                  ? "provider_order_id"
                                  : booking.provider === "easypost"
                                    ? "provider_shipment_id"
                                    : "provider_order_id";
                                await reconcileShipmentBooking(sid, {
                                  outcome: "confirmed_existing",
                                  [key]: providerReference.trim(),
                                });
                                setReconcileTarget("");
                              })}
                            disabled={!providerReference.trim() || !!actionId}
                            className="text-[10px] font-semibold px-2 py-1 bg-emerald-700 text-white rounded-sm disabled:opacity-50">
                      Record existing
                    </button>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <button type="button"
                            onClick={() => runAction(
                              sid, "Booking cleared for safe retry",
                              async () => {
                                await reconcileShipmentBooking(sid, {
                                  outcome: "confirmed_not_created",
                                });
                                setReconcileTarget("");
                              })}
                            disabled={!!actionId}
                            className="text-[10px] font-semibold text-amber-800 hover:underline disabled:opacity-50">
                      No booking exists; allow retry
                    </button>
                    <button type="button"
                            onClick={() => setReconcileTarget("")}
                            className="text-[10px] text-slate-500 hover:underline">
                      Close
                    </button>
                  </div>
                </div>
              )}

              {cancelTarget === sid && (
                <div className="mt-2 space-y-1.5">
                  <input value={cancelReason}
                         onChange={(event) => setCancelReason(event.target.value)}
                         placeholder="Cancellation reason"
                         className="w-full text-[11px] border border-slate-300 rounded-sm px-2 py-1" />
                  {booking.provider === "ch_robinson"
                    && booking.status === "confirmed" && (
                    <input
                      value={cancelConfirmation}
                      onChange={(event) =>
                        setCancelConfirmation(event.target.value)}
                      placeholder="Navisphere cancellation confirmation"
                      data-testid={`shipment-cancel-confirmation-${sid}`}
                      className="w-full text-[11px] border border-slate-300 rounded-sm px-2 py-1"
                    />
                  )}
                  <div className="flex items-center gap-1.5">
                    <button type="button"
                            onClick={() => runAction(
                              sid, "Shipment cancelled",
                              async () => {
                                const result = await cancelShipment(sid, {
                                  reason: cancelReason,
                                  provider_cancellation_confirmation:
                                    cancelConfirmation.trim() || null,
                                });
                                if (!result?.pending) setCancelTarget("");
                                return result;
                              })}
                            disabled={!cancelReason.trim()
                              || (booking.provider === "ch_robinson"
                                && booking.status === "confirmed"
                                && !cancelConfirmation.trim())
                              || !!actionId}
                            className="text-[10px] font-semibold px-2 py-1 bg-rose-700 text-white rounded-sm disabled:opacity-50">
                      Confirm
                    </button>
                    <button type="button" onClick={() => setCancelTarget("")}
                            className="text-[10px] px-2 py-1 border border-slate-300 rounded-sm">
                      Keep
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
